import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Upload, FileText, X, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { UPLOAD } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { trackFileUpload } from '@/lib/analytics';
import SEO from '@/components/SEO';
import AnalysisLoadingScreen from '@/components/AnalysisLoadingScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SITUATION_LABELS = {
  enfant: 'Enfant du défunt',
  conjoint: 'Conjoint survivant',
  fratrie: 'Frère ou sœur',
  autre: 'Autre lien de parenté'
};

const DOCTYPE_LABELS = {
  donation_partage: 'Donation-partage',
  declaration: 'Déclaration de succession',
  notoriete: 'Acte de notoriété / attestation',
  vente: 'Vente immobilière',
  testament: 'Testament',
  courrier: 'Courrier de notaire',
  autre_doc: 'Autre document notarial'
};

const UploadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const onboarding = location.state?.onboarding || null;
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0); // kept for API compatibility; no longer driven by handleAnalyze
  // Loading-screen state. Purely cosmetic — never blocks or alters the
  // business logic of the upload / analyze / poll / navigate flow below.
  const [isUploading, setIsUploading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  }, []);

  const handleFileInput = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const [processingStage, setProcessingStage] = useState(''); // kept; no longer rendered, retained for backward compatibility

  const [errorMessage, setErrorMessage] = useState('');

  const handleAnalyze = async () => {
    if (!file) return;

    setIsAnalyzing(true);
    setIsReady(false);
    setIsUploading(true);
    setProgress(0);
    setErrorMessage('');
    setProcessingStage('');

    try {
      // Real upload via multipart/form-data — business logic untouched.
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const documentId = uploadResponse.data.id;

      // GA4 — file_upload (fires once per successful upload)
      trackFileUpload({
        documentId,
        fileType: file?.type,
        fileName: file?.name,
        fileSize: file?.size,
      });

      // Upload finished. The loading screen can leave the "uploading" hint
      // and run its full 7-stage choreography while the backend works.
      setIsUploading(false);

      const analysisResponse = await axios.post(
        `${API}/documents/${documentId}/analyze`,
        null,
        { timeout: 60000 }
      );

      // The /analyze endpoint now returns immediately. If the work was
      // already cached (analysis_status === "completed"), we navigate
      // straight to the dashboard. Otherwise we poll GET /analyses/{id}
      // every 3 s until the background job is done — this bypasses the
      // ~60 s Kubernetes ingress timeout.
      let finalAnalysis = analysisResponse.data;
      if (finalAnalysis.analysis_status === 'processing') {
        const startedAt = Date.now();
        const MAX_WAIT_MS = 4 * 60 * 1000; // 4 min hard cap
        // eslint-disable-next-line no-await-in-loop
        while (Date.now() - startedAt < MAX_WAIT_MS) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 3000));
          // eslint-disable-next-line no-await-in-loop
          const pollRes = await axios.get(
            `${API}/analyses/${documentId}`,
            { timeout: 15000 }
          );
          const status = pollRes.data?.analysis_status;
          if (status === 'completed' || status === 'failed') {
            finalAnalysis = pollRes.data;
            break;
          }
        }
        if (finalAnalysis.analysis_status === 'processing') {
          throw new Error('TIMEOUT_POLLING');
        }
      }

      // If the background pipeline reported a hard failure, surface the
      // server-side French explanation on the upload page itself rather
      // than navigating to an empty dashboard.
      if (finalAnalysis.analysis_status === 'failed') {
        const detail = finalAnalysis.document_summary
          || "Une difficulté technique est survenue pendant l'analyse. Vous pouvez réessayer dans un instant.";
        setErrorMessage(detail);
        setIsAnalyzing(false);
        setIsUploading(false);
        setIsReady(false);
        setProgress(0);
        return;
      }

      // Success — flip the screen to "ready" (snaps to 100 % + success line),
      // then navigate after the 600 ms acknowledgement window.
      setIsReady(true);
      setProgress(100);
      setTimeout(() => {
        navigate('/dashboard', {
          state: {
            analysis: finalAnalysis,
            document: uploadResponse.data
          }
        });
      }, 600);

    } catch (error) {
      console.error('Error analyzing document:', error);
      const detail =
        error?.response?.data?.detail ||
        "Une difficulté technique est survenue pendant l'analyse. Vous pouvez réessayer dans un instant.";
      setErrorMessage(detail);
      setIsAnalyzing(false);
      setIsUploading(false);
      setIsReady(false);
      setProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3] py-10 sm:py-12 px-5 sm:px-6 md:px-12 lg:px-24">
      <SEO
        title="Importer un document notarié | HÉRITA"
        description="Importez votre acte ou courrier notarial. HÉRITA identifie automatiquement les personnes, les biens, les montants et les conséquences qui vous concernent."
        path="/upload"
        noindex
      />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-10 sm:mb-12">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 text-[#2C363F] hover:text-[#1A2530] font-['Work_Sans']"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <h1 className="font-['Cormorant_Garamond'] font-light text-4xl lg:text-5xl text-[#1A2530] mb-4">
            Ajoutez votre document notarié
          </h1>
          <p className="font-['Work_Sans'] text-lg text-[#2C363F]">
            Importez votre acte ou courrier notarial. Hérita identifie les
            informations importantes et vous explique ce qui vous concerne.
          </p>

          {/* Personalized context from onboarding */}
          {onboarding && (onboarding.situation || onboarding.documentType) && (
            <div className="mt-8 p-5 bg-white rounded-2xl border border-[#E2DBCF]">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E3D5CA] flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-4 w-4 text-[#1A2530]" />
                </div>
                <div className="flex-1">
                  <p className="font-['Work_Sans'] font-semibold text-sm text-[#1A2530] mb-2">
                    Votre profil est enregistré
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {onboarding.situation && (
                      <span className="font-['Work_Sans'] text-xs text-[#2C363F] bg-[#F9F7F3] px-3 py-1 rounded-full">
                        {SITUATION_LABELS[onboarding.situation]}
                      </span>
                    )}
                    {onboarding.documentType && (
                      <span className="font-['Work_Sans'] text-xs text-[#2C363F] bg-[#F9F7F3] px-3 py-1 rounded-full">
                        {DOCTYPE_LABELS[onboarding.documentType]}
                      </span>
                    )}
                  </div>
                  <p className="font-['Work_Sans'] text-xs text-[#2C363F] mt-3">
                    L'analyse sera adaptée à votre situation personnelle.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Upload Area */}
        {!isAnalyzing && errorMessage && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200">
            <p className="font-['Work_Sans'] text-sm text-red-800">{errorMessage}</p>
          </div>
        )}
        {!isAnalyzing && (
          <div
            data-testid={UPLOAD.dropzone}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`bg-white rounded-2xl p-12 border-2 border-dashed transition-all duration-300 ${
              isDragging 
                ? 'border-[#1A2530] bg-[#F9F7F3]' 
                : 'border-[#E2DBCF] hover:border-[#D3CCBE]'
            }`}
          >
            <div className="flex flex-col items-center justify-center">
              {!file ? (
                <>
                  <div className="mb-6">
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/03884dd5-fbbf-4e16-a56d-37b6141ad5dd/images/b3fe73767ebf6b738a6984c45ca3a582ac6fa7b2bf2b1b144d876065fb81557c.png"
                      alt="Upload"
                      className="w-32 h-32 object-contain"
                    />
                  </div>
                  <h3 className="font-['Work_Sans'] font-semibold text-2xl text-[#1A2530] mb-2">
                    Déposez votre document ici
                  </h3>
                  <p className="font-['Work_Sans'] text-base text-[#2C363F] mb-6">
                    ou cliquez pour sélectionner un fichier
                  </p>
                  <input
                    data-testid={UPLOAD.fileInput}
                    type="file"
                    id="file-input"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileInput}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('file-input')?.click()}
                    className="bg-[#1A2530] hover:bg-[#2C363F] text-white px-8 py-3 rounded-full font-['Work_Sans'] font-medium cursor-pointer"
                  >
                    Sélectionner un fichier
                  </Button>
                  <p className="font-['Work_Sans'] text-sm text-[#2C363F] mt-6">
                    Formats acceptés : PDF, JPG, PNG (max 50 Mo)
                  </p>
                </>
              ) : (
                <div data-testid={UPLOAD.uploadedFile} data-clarity-mask="True" className="w-full">
                  <div className="flex items-center justify-between bg-[#F9F7F3] rounded-xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#E3D5CA] rounded-lg flex items-center justify-center">
                        <FileText className="h-6 w-6 text-[#1A2530]" />
                      </div>
                      <div>
                        <p className="font-['Work_Sans'] font-medium text-base text-[#1A2530]">
                          {file.name}
                        </p>
                        <p className="font-['Work_Sans'] text-sm text-[#2C363F]">
                          {(file.size / 1024 / 1024).toFixed(2)} Mo
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setFile(null)}
                      aria-label="Retirer le fichier sélectionné"
                      className="hover:bg-[#E2DBCF] rounded-full"
                    >
                      <X className="h-5 w-5 text-[#2C363F]" aria-hidden="true" />
                    </Button>
                  </div>
                  
                  <Button
                    data-testid={UPLOAD.analyzeButton}
                    onClick={handleAnalyze}
                    className="w-full mt-6 bg-[#1A2530] hover:bg-[#2C363F] text-white py-4 rounded-full font-['Work_Sans'] font-semibold text-lg"
                  >
                    Analyser mon document
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isAnalyzing && (
          <div data-testid={UPLOAD.loadingState}>
            <AnalysisLoadingScreen
              active={isAnalyzing}
              isReady={isReady}
              isUploading={isUploading}
            />
          </div>
        )}

        {/* Info Cards */}
        {!isAnalyzing && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-[#E2DBCF]">
              <h4 className="font-['Work_Sans'] font-semibold text-base text-[#1A2530] mb-2">
                Documents acceptés
              </h4>
              <ul className="space-y-2">
                <li className="font-['Work_Sans'] text-sm text-[#2C363F]">• Donation-partage · Succession · Testament</li>
                <li className="font-['Work_Sans'] text-sm text-[#2C363F]">• Vente immobilière · Attestation immobilière</li>
                <li className="font-['Work_Sans'] text-sm text-[#2C363F]">• Partage · Indivision · SCI</li>
                <li className="font-['Work_Sans'] text-sm text-[#2C363F]">• Courrier notarial</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-[#E2DBCF]">
              <h4 className="font-['Work_Sans'] font-semibold text-base text-[#1A2530] mb-2">
                Sécurité garantie
              </h4>
              <p className="font-['Work_Sans'] text-sm text-[#2C363F]">
                Vos documents sont chiffrés et ne sont jamais partagés. Nous respectons votre vie privée et la confidentialité de vos informations.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadPage;